import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import RoomCarousel from '../components/RoomCarousel';
import FacilityStrip from '../components/FacilityStrip';
import FoodGallery from '../components/FoodGallery';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import './Home.css';

export default function Home() {
  const { activeLang, t } = useLanguage();
  const [heroSlides, setHeroSlides] = useState([]);
  const [welcomeIndex, setWelcomeIndex] = useState(0);
  const [roomHeroSlides, setRoomHeroSlides] = useState([]);
  const [roomHeroInitialIndex, setRoomHeroInitialIndex] = useState(0);
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    api.get('/content/hero').then(res => {
      setHeroSlides(res.data || []);
    }).catch(() => {});

    api.get(`/rooms?lang=${activeLang}`).then(res => {
      const rooms = res.data;
      const heroMedia = rooms
        .filter(r => r.featured && (r.hero_video_url || r.hero_image_url))
        .map(r => ({
          type: r.hero_video_url ? 'video' : 'image',
          url: r.hero_video_url || r.hero_image_url,
          name: r.name,
          slug: r.slug,
          is_main: !!r.is_main,
          category_group: r.category_group,
        }));
      setRoomHeroSlides(heroMedia);
      const mainIdx = heroMedia.findIndex(r => r.is_main);
      setRoomHeroInitialIndex(mainIdx >= 0 ? mainIdx : 0);
    }).catch(() => {});

    api.get(`/services?lang=${activeLang}`).then(res => {
      const all = res.data?.services || [];
      setFacilities(all.filter(s => s.category === 'facility'));
    }).catch(() => {});
  }, [activeLang]);

  // navigare manuală — fără auto-advance pentru welcome gallery

  const l = (ro, en, de, fr) => {
    if (activeLang === 'en') return en;
    if (activeLang === 'de') return de;
    if (activeLang === 'fr') return fr;
    return ro;
  };

  return (
    <div className="home">

      {/* 1. Welcome / Intro — gallery sus, text jos */}
      <section className="section home__welcome">
        {heroSlides.length > 0 && (
          <div className="home__welcome-gallery-wrap">
            <div className="home__welcome-gallery">
              <AnimatePresence mode="wait" initial={true}>
                {heroSlides[welcomeIndex] && (
                  heroSlides[welcomeIndex].type === 'video'
                    ? <motion.video
                        key={`slide-${welcomeIndex}`}
                        src={heroSlides[welcomeIndex].url}
                        className="home__welcome-gallery-item"
                        autoPlay muted loop playsInline
                        initial={{ opacity: 0, scale: 1.0 }}
                        animate={{ opacity: 1, scale: 1.18 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: 0.9, ease: 'easeOut' }, scale: { duration: 8, ease: 'easeIn' } }}
                      />
                    : <motion.img
                        key={`slide-${welcomeIndex}`}
                        src={heroSlides[welcomeIndex].url}
                        alt="Eden"
                        className="home__welcome-gallery-item"
                        initial={{ opacity: 0, scale: 1.0 }}
                        animate={{ opacity: 1, scale: 1.18 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: 0.9, ease: 'easeOut' }, scale: { duration: 8, ease: 'easeIn' } }}
                      />
                )}
              </AnimatePresence>
            </div>
            {heroSlides.length > 1 && (
              <>
                <div className="home__welcome-dots">
                  {heroSlides.map((_, i) => (
                    <button
                      key={i}
                      className={`home__welcome-dot${i === welcomeIndex ? ' active' : ''}`}
                      onClick={() => setWelcomeIndex(i)}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  className="home__welcome-arrow home__welcome-arrow--prev"
                  onClick={() => setWelcomeIndex(i => (i - 1 + heroSlides.length) % heroSlides.length)}
                  aria-label="Slide anterior"
                >&#8592;</button>
                <button
                  className="home__welcome-arrow home__welcome-arrow--next"
                  onClick={() => setWelcomeIndex(i => (i + 1) % heroSlides.length)}
                  aria-label="Slide următor"
                >&#8594;</button>
              </>
            )}
            <div className="home__welcome-overlay" />
            <div className="home__welcome-hero">
              <h1 className="home__welcome-hero-title">{t('site_name', 'Eden Pensiune')}</h1>
              <p className="home__welcome-hero-sub">{t('site_tagline', 'Cazare la malul mării, locuri autentice')}</p>
            </div>
          </div>
        )}
        <div className="container">
          <div className="home__welcome-text">
            <span className="section-eyebrow">{t('welcome_eyebrow', 'Bine ați venit')}</span>
            <h2 className="home__welcome-title">
              {t('welcome_title', 'Un refugiu la malul mării')}
            </h2>
            <p className="home__welcome-desc">
              {t('welcome_text', 'Descoperă liniștea și eleganța pensiunii Eden — un loc în care fiecare detaliu este gândit pentru confortul tău.')}
            </p>
            <Link to="/despre" className="home__welcome-link">
              {l('Despre noi', 'About us', 'Über uns', 'À propos')} →
            </Link>
          </div>
        </div>
      </section>

      {/* 2. Room Carousel — Media Hero din camere */}
      <RoomCarousel
        rooms={roomHeroSlides}
        initialIndex={roomHeroInitialIndex}
        title={t('rooms_section_title', 'Cazare la malul mării')}
        subtitle={t('rooms_section_subtitle', 'Camere elegante, priveliști unice și o atmosferă de neuitat')}
        ctaLabel={l('Explorează camera', 'Explore room', 'Zimmer entdecken', 'Explorer la chambre')}
        allRoomsLabel={l('Toate camerele', 'All rooms', 'Alle Zimmer', 'Toutes les chambres')}
      />

      {/* 4. Gastronomie teaser */}
      <section className="section home__gastro">
        <div className="container">
          <div className="home__gastro-grid">
            <div className="home__gastro-gallery">
              <FoodGallery compact lang={activeLang} />
            </div>
            <div className="home__gastro-text">
              <span className="section-eyebrow">{t('gastro_eyebrow', 'Gastronomie')}</span>
              <h2 className="home__gastro-title">
                {t('gastro_title', 'Savori autentice')}
              </h2>
              <p className="home__gastro-desc">
                {t('gastro_text', 'Bucătăria noastră îmbină tradițiile locale cu ingrediente proaspete, oferind o experiență culinară memorabilă în fiecare zi.')}
              </p>
              <Link to="/meniu" className="home__gastro-link">
                {t('gastro_link', 'Meniu')} →
              </Link>
            </div>
          </div>
        </div>
      </section>
      {facilities.length > 0 && (
        <section className="section home__facilities">
          <div className="container">
            <div className="home__facilities-header">
              <span className="section-eyebrow">{t('facilities_eyebrow', 'Servicii & Facilități')}</span>
              <h2 className="section-title">{t('facilities_title', 'Experiența completă')}</h2>
            </div>
          </div>
          <FacilityStrip facilities={facilities} />
        </section>
      )}

      {/* 7. CTA rezervare — charcoal */}
      <section className="home__cta-section">
        <div className="container">
          <div className="home__cta-inner">
            <span className="section-eyebrow home__cta-eyebrow">{t('cta_eyebrow', 'Rezervare')}</span>
            <h2>{t('cta_title', 'Pregătit pentru un sejur relaxant?')}</h2>
            <p>{t('cta_text', 'Contactați-ne pentru disponibilitate și prețuri personalizate.')}</p>
            <Link to="/contact" className="btn home__cta-btn">
              {l('Contactați-ne', 'Contact us', 'Kontakt', 'Contactez-nous')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

